// MUST be the first import — defines `ngDevMode` globally before federation
// hydration loads chunks that read it. See ng-runtime-shim.ts for context.
import './ng-runtime-shim';
// Registers the design-system <klaro-icon> web component (name-based Lucide
// registry, shared with klaro-admin) so the secondary-panel menu can render
// icons by name — the menu-service ships icon *names*, not SVG path data.
import './app/shared/klaro-icons';

// sockjs-client (transitive dep of @stomp/stompjs SockJS support — used by
// klaro-chat, and arriving in klaro-call / klaro-email next) references
// Node's `global` keyword, which browsers don't define and modern Angular
// esbuild doesn't auto-shim. The shell loads the chat remote at runtime, so
// this polyfill MUST run here too — the remote's own main.ts is never
// executed under federated mounting. See docs/build-log.md >
// "sockjs-client global polyfill".
(globalThis as { global?: unknown }).global = globalThis;

import { environment } from './environments/environment';
import { installMockApi, ensureDefaultPersona } from './mocks/mock-api';
import { loadClarity } from './app/core/analytics/clarity';
import { initFederation } from '@angular-architects/native-federation';

// MSW mock intercepts are only active in LOCAL dev to avoid polluting the DEPLOYED
// dev/qa/uat/prod environments, which rely entirely on real backend services.
// NOTE: environment.envName is 'dev' for BOTH local `ng serve` (environment.ts) AND
// the deployed dev environment (environment.dev.ts), so gating on envName wrongly
// enabled MSW on klaro-business-dev (it intercepted /api/v1/shell/auth/login and the
// real auth call never reached api.hiklaro.com/dev/klaro-auth). Gate on the hostname
// instead — the same local-only check `resolveManifestUrl()` uses in
// `app/core/federation/load-remote.ts`, which now owns manifest resolution.
if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
  installMockApi();
  ensureDefaultPersona();
}

// Microsoft Clarity (session replay + heatmaps). Loaded here — before
// federation and Angular bootstrap — so the full session is captured. Gated
// per environment (environment.clarity) and hard-skipped on localhost, so
// local dev is never recorded. See app/core/analytics/clarity.ts.
loadClarity(environment.clarity);

/**
 * Federation init is HOST-ONLY — no remotes are passed in.
 *
 * `initFederation(manifestUrl)` fetches the manifest and then, inside
 * `processRemoteInfos`, fans out `Promise.all` over EVERY remote listed in it
 * and fetches each `remoteEntry.json` before resolving. Since the manifest is
 * a static file naming the whole catalogue — not the org's installed apps —
 * that made boot cost O(number of apps in the catalogue) for every user: an
 * org with four apps installed still paid for all eleven, and each new app
 * added a request to the critical path ahead of `import('./bootstrap')`.
 *
 * Passing `{}` keeps everything the host needs (it still fetches the shell's
 * own `remoteEntry.json` via `processHostInfo`, which is what seeds the shared
 * dependency registry) and skips the remote fan-out entirely. Boot is now two
 * same-origin requests regardless of how large the catalogue grows.
 *
 * Remotes are registered on first use instead — see
 * `app/core/federation/load-remote.ts`. Native Federation supports this
 * directly: `loadRemoteModule({ remoteEntry, exposedModule })` runs
 * `ensureRemoteInitialized`, which fetches the remote's `remoteEntry.json`,
 * registers it, and appends its import map to the document. Shared singletons
 * still resolve, because `processRemoteImports` consults the same
 * `externals` registry the host seeded here — a late-registered remote maps
 * `@angular/core` to the host's already-loaded copy rather than its own.
 *
 * The previous 8s timeout race is gone with the fan-out that motivated it. It
 * existed because one unreachable remote could hang boot on an OS TCP wait; a
 * host-only init touches only the origin already serving this document, so if
 * that request hangs the app has no bundles to run either way. An unreachable
 * remote is now a per-route failure at navigation time, which is where the
 * existing `.catch` handling and chunk-recovery already live.
 */
initFederation({})
  .catch((err) => {
    console.error('[shell] initFederation failed — booting without shared scope.', err);
  })
  // Tenant classification MUST precede ./bootstrap: AuthService reads
  // tenant-scoped storage keys and APP_BASE_HREF derives from the classified
  // path prefix when Angular's injector is built. It is a DYNAMIC import on
  // purpose — '@klaro/shared' (like every workspace lib) is a federation-
  // shared external resolved through the import map initFederation just
  // wrote; a static import at the top of this file fails bare-specifier
  // resolution and kills the host entry before anything boots.
  //
  // The rollout flag gates CLASSIFICATION itself, not just resolution: with
  // it off, every address classifies as root (today's behaviour, base href
  // '/') — otherwise a stray /foo/bar URL on a flag-off environment would
  // shift the router's base and turn a plain 404 into a mis-based app.
  .then(async () => {
    try {
      const { installTenantBridge, updateTenantBridge } = await import('@klaro/shared');
      if (environment.tenantRoutingEnabled) {
        installTenantBridge();
      } else {
        updateTenantBridge({ mode: 'root', slug: null, pathPrefix: '' });
      }
    } catch (err) {
      // Same degraded mode as an initFederation failure: publish a root
      // context so whatever still boots does not throw on the gate.
      console.error('[shell] tenant classification failed — defaulting to root context.', err);
      window.__klaroTenant = { mode: 'root', slug: null, pathPrefix: '' };
    }
  })
  .then(() => import('./bootstrap'))
  .catch((err) => console.error('[shell] bootstrap import failed', err));
